"use client";

import { ArrowRight, Check, EyeOff, MessageSquareWarning, ShieldCheck, Smartphone, SlidersHorizontal, UserRoundX, Users, Wallet } from "lucide-react";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { usePrefs } from "@/components/prefs-provider";
import { ButtonLink } from "@/components/ui/button";

function Eyebrow({ children, light }: { children: React.ReactNode; light?: boolean }) {
  return (
    <p className={`text-xs font-bold uppercase tracking-[0.16em] ${light ? "text-brand-soft" : "text-brand"}`}>{children}</p>
  );
}

export default function LandingPage() {
  const { t } = usePrefs();
  const l = t.landing;

  return (
    <div>
      <SiteHeader />

      <section className="hero-mesh flex min-h-[100dvh] flex-col items-center justify-center px-4 pb-16 pt-20 text-center">
        <h1 className="max-w-xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-ink sm:text-5xl">{l.h1}</h1>
        <p className="mt-4 max-w-md text-lg leading-relaxed text-ink-2">{l.lead}</p>
        <ButtonLink
          href="/register"
          native
          className="mt-8 h-14 w-full max-w-sm text-lg"
          icon={<ArrowRight className="h-5 w-5 rtl:-scale-x-100" aria-hidden />}
          iconPosition="end"
        >
          {l.cta}
        </ButtonLink>
        <a href="/login" className="mt-4 text-base font-medium text-ink-2 underline underline-offset-4">
          {l.haveAccount}
        </a>
        <p className="mt-3 text-sm text-ink-2">{l.freeNow}</p>
      </section>

      <section className="border-y border-line bg-surface py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="max-w-2xl">
            <Eyebrow>{l.reality}</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">{l.realityH2}</h2>
            <p className="mt-4 text-lg leading-relaxed text-ink-2">{l.realityP}</p>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              { title: l.pain1t, body: l.pain1b },
              { title: l.pain2t, body: l.pain2b },
              { title: l.pain3t, body: l.pain3b },
            ].map((item) => (
              <div key={item.title} className="border-t-2 border-brand pt-5">
                <h3 className="font-display text-xl font-bold text-ink">{item.title}</h3>
                <p className="mt-3 leading-relaxed text-ink-2">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="scroll-mt-20 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>{l.how}</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">{l.howH2}</h2>
          </div>
          <ol className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { Icon: Users, title: l.step1t, body: l.step1b },
              { Icon: Smartphone, title: l.step2t, body: l.step2b },
              { Icon: ShieldCheck, title: l.step3t, body: l.step3b },
            ].map((item, i) => (
              <li key={item.title} className="relative rounded-2xl border border-line bg-surface p-6 shadow-1">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft text-brand-ink">
                    <item.Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <span className="tabular font-display text-3xl font-bold text-line-strong" aria-hidden>
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-xl font-bold text-ink">{item.title}</h3>
                <p className="mt-2 leading-relaxed text-ink-2">{item.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="protection" className="scroll-mt-20 bg-brand-strong py-20 text-on-brand">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="max-w-2xl">
            <Eyebrow light>{l.prot}</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{l.protH2}</h2>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { Icon: MessageSquareWarning, title: l.f1t, body: l.f1b },
              { Icon: EyeOff, title: l.f2t, body: l.f2b },
              { Icon: Wallet, title: l.f3t, body: l.f3b },
              { Icon: Users, title: l.f4t, body: l.f4b },
              { Icon: UserRoundX, title: l.f5t, body: l.f5b },
              { Icon: SlidersHorizontal, title: l.f6t, body: l.f6b },
            ].map((f) => (
              <div key={f.title} className="rounded-2xl border border-white/10 bg-white/[0.06] p-5">
                <f.Icon className="h-5 w-5 text-brand-soft" aria-hidden />
                <h3 className="mt-4 text-lg font-bold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/75">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="scroll-mt-20 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>{l.pricing}</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-ink sm:text-5xl">{l.pricingH2}</h2>
            <p className="mt-4 text-lg leading-relaxed text-ink-2">{l.pricingP}</p>
          </div>

          <div className="mx-auto mt-12 grid max-w-4xl items-center gap-8 rounded-3xl border border-line bg-surface p-8 shadow-2 md:grid-cols-[0.9fr_1.1fr] md:p-12">
            <div className="text-center md:text-start">
              <p className="font-display text-6xl font-bold leading-none tracking-tight text-brand sm:text-7xl">{l.pricingPrice}</p>
              <p className="mt-3 text-lg font-semibold text-ink">{l.pricingUnit}</p>
              <p className="mt-2 inline-block rounded-full bg-brand-soft px-3 py-1 text-sm font-bold text-brand-ink">{l.pricingPerDay}</p>
            </div>
            <div>
              <ul className="space-y-3">
                {l.pricingPoints.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ok-soft text-ok-ink">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
                    </span>
                    <span className="leading-relaxed text-ink">{p}</span>
                  </li>
                ))}
              </ul>
              <ButtonLink native href="/register" size="lg" className="mt-8 w-full sm:w-auto" icon={<ArrowRight className="h-4 w-4 rtl:-scale-x-100" aria-hidden />} iconPosition="end">
                {l.pricingCta}
              </ButtonLink>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="rounded-3xl bg-brand-soft px-6 py-14 text-center sm:px-12">
            <h2 className="font-display text-3xl font-bold tracking-tight text-brand-ink sm:text-4xl">{l.ctaH2}</h2>
            <p className="mx-auto mt-4 max-w-xl leading-relaxed text-ink-2">{l.ctaP}</p>
            <div className="mt-8 flex justify-center">
              <ButtonLink native href="/register" size="lg" className="h-14 w-full max-w-sm text-lg" icon={<ArrowRight className="h-5 w-5 rtl:-scale-x-100" aria-hidden />} iconPosition="end">
                {l.cta}
              </ButtonLink>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
